import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './CallButton.styles';
import { PhoneIcon } from '@/components/UI/Icons/Icons';
import { PhonesBottomSheet } from '@/components/PhonesBottomSheet/PhonesBottomSheet';
import { useOpenContacts } from '@/hooks/useOpenContacts';
import { useAdDetails } from '@/hooks/useAdDetails';
import { useFloatingButtonsVisibility } from '@/hooks/useFloatingButtonsVisibility';
import { useServerDataContext } from '@/contexts';
import { useResponsive } from '@/hooks/useResponsive';
import { cleanPhoneNumber } from '@/utils/helpers';
import { usePhoneNumbersStore } from '@/store/usePhoneNumbersStore';
import { WarningMessage } from '@/components/Ad/WarningMessage';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';
import { AdStatus } from '@/types';
import { useIsAuthor } from '@/hooks/useIsAuthor';

interface Props {
  dataTestId?: string;
}

export const CallButton: FC<Props> = ({ dataTestId = 'call-button' }) => {
  const isBrowser = typeof window !== 'undefined';
  const { t } = useTranslation();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const { isCallsAvailable } = useFloatingButtonsVisibility();
  const { isDesktop } = useResponsive();
  const { isContactsOpen, toggleContacts, getPhoneNumbers, phoneNumbers } =
    useOpenContacts({ shouldStartCall: !isDesktop });
  const theme = useTheme();
  const removeShownNumbersAdId = usePhoneNumbersStore(
    state => state.removeShownNumbersAdId
  );
  const isAuthor = useIsAuthor();
  const { categoryDetails } = useCurrentCategoryDetails({
    skip: !isBrowser,
  });

  const adData = adDetails ?? initialAdDetails;
  const { id, status, shop, contact } = adData ?? {};
  const isWarningVisible =
    isDesktop &&
    !isAuthor &&
    !shop &&
    (status === AdStatus.Published || status === AdStatus.Expired);
  const classNames = isDesktop
    ? 'show-phones js-show-phones active'
    : 'fixed-btn fixed-btn--phone js-bottom-phone';

  useEffect(() => {
    return () => {
      if (!adData?.id) return;
      removeShownNumbersAdId(adData.id);
    };
  }, [adData?.id]);

  if (!id || !isCallsAvailable) return null;

  return (
    <>
      {isDesktop && phoneNumbers.length > 0 ? (
        <S.RevealedContainer>
          {phoneNumbers.map(phoneNumber => (
            <S.RevealedPhoneNumber
              key={phoneNumber}
              href={`tel:${cleanPhoneNumber(phoneNumber)}`}
            >
              {phoneNumber}
            </S.RevealedPhoneNumber>
          ))}

          {isWarningVisible && categoryDetails?.warningMessage && (
            <WarningMessage message={categoryDetails.warningMessage} />
          )}
        </S.RevealedContainer>
      ) : (
        <S.CallButton
          data-testid={dataTestId}
          appearance="green"
          borderRadius={14}
          isFullWidth
          onClick={() =>
            getPhoneNumbers({
              adId: id,
              isFloatingButtonClicked: !isDesktop,
            })
          }
          className={classNames}
        >
          {isDesktop ? (
            <S.Container>
              <S.Row>{t('common.show_phone')}</S.Row>
              <S.Row>
                <S.ButtonIconBox>
                  <PhoneIcon />
                </S.ButtonIconBox>
                <S.PhoneNumber>{contact?.phones[0]}</S.PhoneNumber>
              </S.Row>
            </S.Container>
          ) : (
            <>
              <S.ButtonIconBox>
                <PhoneIcon />
              </S.ButtonIconBox>{' '}
              {t('common.call')}
            </>
          )}
        </S.CallButton>
      )}
      {!isDesktop && (
        <PhonesBottomSheet
          isOpen={isContactsOpen}
          close={toggleContacts}
          phones={phoneNumbers}
          color={theme.palette.system.green}
        />
      )}
    </>
  );
};
